import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BsPlusLg } from "react-icons/bs";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import Handler from "../../Helpers/Handler";
import { formatDateTime, showZoneTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import EmptyState from "../../Components/List/EmptyState";
import ErrorState from "../../Components/List/ErrorState";
import useListParams from "../../Helpers/useListParams";

// "Permission" here is short time off during a shift - the staff-request sense,
// not the capability/ACL sense used everywhere else in this panel.
const PERMISSION_TYPES = [
    { value: "PERSONAL", label: "Personal" },
    { value: "MEDICAL", label: "Medical" },
    { value: "OTHER", label: "Other" },
];

const STATUS_CLASS = { PENDING: "att-warn", APPROVED: "att-ok", REJECTED: "att-bad" };
const COUNT = 10;

// The backend allows 30 to 60 minutes. Offering the exact allowed lengths as a
// list beats asking someone to nudge an end time until the gap happens to land
// in range - the old two-pickers form made it far too easy to sit at zero.
const DURATIONS = [30, 35, 40, 45, 50, 55, 60];
const durationLabel = (minutes) => (minutes === 60 ? "1 hour" : `${minutes} minutes`);

const pad = (value) => String(value).padStart(2, "0");
const toIsoDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const toClock = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const readClock = (date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const defaultForm = () => {
    // Half an hour out, rounded up to the next 5 minutes so the field opens on
    // a round time rather than 14:37.
    const start = new Date(Date.now() + 30 * 60000);
    start.setSeconds(0, 0);
    start.setMinutes(Math.ceil(start.getMinutes() / 5) * 5);
    return {
        permissionType: PERMISSION_TYPES[0].value,
        date: toIsoDay(start),
        startTime: toClock(start),
        durationMinutes: 45,
        reason: "",
    };
};

const PermissionTab = () => {
    const [list, setList] = useState([]);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Page number in the URL path, the status filter in the query string (next to the page's ?tab=) - see Helpers/useListParams.
    const { page, setPage, filters, setFilter } = useListParams({ status: "" }, { zeroBased: true });
    const statusFilter = filters.status;
    const setStatusFilter = (value) => setFilter("status", value);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async (pageNo) => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/staff/permission/list/${pageNo}`,
            params: { count: COUNT },
            data: { ...(statusFilter ? { status: statusFilter } : {}), sortBy: "createdAt", sort: true },
        });
        if (response?.success && response?.data?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setHasNext(Boolean(data.next));
            setError(null);
        } else {
            setList([]);
            setError(response?.data?.message || "Unable to load your permission requests");
        }
        setLoading(false);
    }, [statusFilter]);

    useEffect(() => { load(page); }, [load, page]);

    // The window is derived, never typed: one date, one start time, one length.
    // Nothing the person picks can produce an invalid gap.
    const fromDate = (() => {
        if (!form.date || !form.startTime) return null;
        const [hours, minutes] = String(form.startTime).split(":").map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        const [year, month, day] = form.date.split("-").map(Number);
        return new Date(year, month - 1, day, hours, minutes, 0, 0);
    })();
    const toDate = fromDate ? new Date(fromDate.getTime() + form.durationMinutes * 60000) : null;

    const submit = async () => {
        if (!fromDate) { toast.error("Pick a date and a start time"); return; }
        // Mirrors the backend rule so the person sees it here rather than as a
        // rejection afterwards. The backend still enforces it.
        if (fromDate <= new Date()) { toast.error("Permission cannot start in the past"); return; }
        if (!form.reason.trim()) { toast.error("Please give a reason"); return; }
        setSaving(true);
        const response = await Handler({
            method: "post",
            url: "/staff/permission/register",
            data: {
                permissionType: form.permissionType,
                fromTime: fromDate.toISOString(),
                toTime: toDate.toISOString(),
                reason: form.reason.trim(),
            },
        });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || "Permission applied");
            setForm(defaultForm());
            setShowForm(false);
            await load(0);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to apply for permission"));
        }
        setSaving(false);
    };

    const change = (event) => setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));

    return (
        <div className="card att-panel-card">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <div>
                        <h6 className="fw-semibold mb-0">Permission requests</h6>
                        <div className="text-muted small">Short time off during a shift, 30 to 60 minutes, and 1 hour in total per day.</div>
                    </div>
                    <div className="d-flex gap-2">
                        <select
                            className="form-select form-select-sm att-filter-select"
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                        </select>
                        <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => setShowForm((prev) => !prev)}>
                            <BsPlusLg /> Apply
                        </button>
                    </div>
                </div>

                {showForm ? (
                    <div className="att-request-form">
                        <div className="row g-3">
                            <div className="col-12 col-md-3">
                                <label className="form-label" htmlFor="perm-type">Type</label>
                                <select id="perm-type" name="permissionType" className="form-select" value={form.permissionType} onChange={change}>
                                    {PERMISSION_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                </select>
                            </div>
                            <div className="col-6 col-md-3">
                                <label className="form-label">Date</label>
                                <DatePicker
                                    format="YYYY-MM-DD"
                                    minDate={new Date(new Date()?.setHours(new Date()?.getHours() - 1))}
                                    containerClassName="w-100"
                                    inputClass="form-control"
                                    value={form.date ? new DateObject(form.date) : ""}
                                    onChange={(date) => { if (date) setForm((prev) => ({ ...prev, date: toIsoDay(date.toDate()) })); }}
                                />
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="form-label" htmlFor="perm-start">Starts at</label>
                                {/* A plain time field rather than the picker's spinner - the
                                    spinner made setting a time unnecessarily fiddly. */}
                                <input
                                    id="perm-start"
                                    type="time"
                                    step={300}
                                    className="form-control"
                                    value={form.startTime}
                                    onChange={(event) => setForm((prev) => ({ ...prev, startTime: event.target.value }))}
                                />
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="form-label" htmlFor="perm-duration">For</label>
                                <select
                                    id="perm-duration"
                                    className="form-select"
                                    value={form.durationMinutes}
                                    onChange={(event) => setForm((prev) => ({ ...prev, durationMinutes: Number(event.target.value) }))}
                                >
                                    {DURATIONS.map((minutes) => <option key={minutes} value={minutes}>{durationLabel(minutes)}</option>)}
                                </select>
                            </div>
                            <div className="col-6 col-md-2 d-flex align-items-start">
                                <button type="button" className="btn btn-primary w-100 mt-md-4" disabled={saving} onClick={submit}>
                                    {saving ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                            <div className="col-12">
                                {fromDate && toDate ? (
                                    <div className="att-window-preview">
                                        You'll be away <strong>{readClock(fromDate)}</strong> to <strong>{readClock(toDate)}</strong>
                                        <span className="text-muted"> · {durationLabel(form.durationMinutes)}</span>
                                    </div>
                                ) : null}
                            </div>
                            <div className="col-12">
                                <label className="form-label" htmlFor="perm-reason">Reason</label>
                                <textarea
                                    id="perm-reason"
                                    name="reason"
                                    className="form-control"
                                    rows={2}
                                    maxLength={500}
                                    placeholder="Why do you need this time?"
                                    value={form.reason}
                                    onChange={change}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}

                {loading ? (
                    <>{[0, 1, 2].map((i) => <Skeleton key={i} height={24} className="mb-2" />)}</>
                ) : error ? (
                    <ErrorState message={error} onRetry={() => load(0)} />
                ) : !list.length ? (
                    <EmptyState reason={statusFilter ? "filter" : "empty"} title="No permission requests" description="Short time-off requests you submit will appear here." />
                ) : (
                    <>
                        <div className={`list-table-wrap`}>
                            <div className='table-responsive list-table-scroll'>
                                <table className="table align-middle mb-0 list-table">
                                    <thead className='sticky-top'>
                                        <tr>
                                            {/* <th>Request</th> */}
                                            <th className="py-3 px-3 list-th ">Type</th>
                                            <th className="py-3 px-3 list-th ">Window</th>
                                            <th className="py-3 px-3 list-th ">Duration</th>
                                            <th className="py-3 px-3 list-th ">Reason</th>
                                            <th className="py-3 px-3 list-th ">Status</th>
                                            <th className="py-3 px-3 list-th ">Decided by</th>
                                            <th className="py-3 px-3 list-th ">Applied</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((row) => (
                                            <tr key={row.permissionId}>
                                                {/* <td className="fw-semibold">{row.permissionId}</td> */}
                                                <td>{textCapitalize(row.permissionType)}</td>
                                                <td>{showZoneTime(row.fromTime)} → {showZoneTime(row.toTime)}</td>
                                                <td>{row.durationMinutes}m</td>
                                                <td className="att-reason-cell">{row.reason || "-"}</td>
                                                <td><span className={`att-chip ${STATUS_CLASS[row.status] || "att-muted"}`}>{textCapitalize(row.status)}</span></td>
                                                <td>{row.approvedBy || "-"}</td>
                                                <td className="text-muted">{formatDateTime(row.createdAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!hasNext} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PermissionTab;
